/* eslint-disable max-len */
// React Imports
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Lodash
import _ from 'lodash';

// I18n
import { Translate } from 'react-redux-i18n';

// React Spectrum Components
import {
  Checkbox,
  Heading,
  ProgressCircle,
  View,
} from '@adobe/react-spectrum';

// React Router
import { withRouter } from '../withRouter';

// Redux Actions
import {
  addSingleFilter,
  removeSingleFilter,
  getPreviewMatches,
} from '../../actions/filterActions';

// Utils
import { addCommaSeparator } from '../../util/util';

// Styles
import '../../styles/FilterCommon.scss';
import '../../styles/MultiSelectFilter.scss';

const MultiSelectFilter = ({
  filterName,
  appliedFilters,
  facets,
  resultsCount,
  addSingleFilterAction,
  getPreviewMatchesAction,
  removeSingleFilterAction,
  router,
}) => {
  const [selected, setSelected] = useState(_.has(appliedFilters, filterName) ? appliedFilters[filterName] : []);
  const prevAppliedFilters = useRef(appliedFilters);

  useEffect(() => {
    if (
      (prevAppliedFilters.current && prevAppliedFilters.current !== appliedFilters && _.size(appliedFilters[filterName]) === 0)
      || (filterName === 'country' && prevAppliedFilters.current?.location?.geo !== appliedFilters.location?.geo)
    ) {
      setSelected([]);
    }
    prevAppliedFilters.current = appliedFilters;
    setSelected(_.has(appliedFilters, filterName) ? appliedFilters[filterName] : []);
  }, [appliedFilters, filterName]);

  const clearFilter = () => {
    setSelected([]);
    removeSingleFilterAction({ name: filterName }, router);
  };

  const updateSelectedOptions = (option) => {
    if (selected.includes(option)) {
      const filteredArray = selected.filter((item) => item !== option);
      setSelected(filteredArray);
      if (filteredArray.length > 0) {
        addSingleFilterAction({ name: filterName, value: filteredArray }, router);
      } else {
        clearFilter();
      }
    } else {
      const updatedSelected = [...selected, option];
      setSelected(updatedSelected);
      if (updatedSelected.length > 0) {
        addSingleFilterAction({ name: filterName, value: updatedSelected }, router);
      } else {
        clearFilter();
      }
    }
  };

  const handleCheckboxState = (item) => selected.includes(item);

  const renderMultiSelectFilter = () => {
    const checkBoxes = facets[filterName].options.map((item) => (
      <Checkbox
        key={item.id}
        isSelected={handleCheckboxState(item.id)}
        onChange={() => updateSelectedOptions(item.id)}
        marginBottom={5}
        marginTop={5}
        UNSAFE_style={{ paddingRight: '0px' }}
      >
        {item.name}
      </Checkbox>
    ));

    const renderPreviewResults = (
      facets[filterName].loadingPreviewMatches ? (
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      ) : (
        <Heading level={4}>
          <Translate
            value="partner_finder.filters.preview_results"
            resultsCount={
                facets[filterName].previewMatches !== null
                  ? addCommaSeparator(facets[filterName].previewMatches)
                  : addCommaSeparator(resultsCount)
              }
          />
        </Heading>
      )
    );

    return (
      <View UNSAFE_className="msf-dialog">
        <div className="checkbox-container">
          {checkBoxes}
        </div>
      </View>
    );
  };

  return (
    <div className="cmp-multiSelectFilter" id={`${filterName}-filter`}>
      {renderMultiSelectFilter()}
    </div>
  );
};

MultiSelectFilter.propTypes = {
  filterName: PropTypes.string.isRequired,
  addSingleFilterAction: PropTypes.func.isRequired,
  getPreviewMatchesAction: PropTypes.func.isRequired,
  removeSingleFilterAction: PropTypes.func.isRequired,
  resultsCount: PropTypes.number.isRequired,
  facets: PropTypes.shape({
    solution: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.object),
      previewMatches: PropTypes.number,
      loadingPreviewMatches: PropTypes.bool,
    }),
    industry: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.object),
      previewMatches: PropTypes.number,
      loadingPreviewMatches: PropTypes.bool,
    }),
    level: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.object),
      previewMatches: PropTypes.number,
      loadingPreviewMatches: PropTypes.bool,
    }),
    initiatives: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.object),
      previewMatches: PropTypes.number,
      loadingPreviewMatches: PropTypes.bool,
    }),
  }).isRequired,
  appliedFilters: PropTypes.shape({
    solution: PropTypes.arrayOf(PropTypes.string),
    industry: PropTypes.arrayOf(PropTypes.string),
    level: PropTypes.arrayOf(PropTypes.string),
    location: PropTypes.any,
    country: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  router: PropTypes.shape({}).isRequired,
};

MultiSelectFilter.defaultProps = {
};

const mapStateToProps = (state) => ({
  locale: state.i18n.locale,
  resultsCount: state.results.totalResults,
  facets: state.facets,
  appliedFilters: state.filter.selected,
});

const mapDispatchToProps = {
  addSingleFilterAction: addSingleFilter,
  getPreviewMatchesAction: getPreviewMatches,
  removeSingleFilterAction: removeSingleFilter,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MultiSelectFilter));
